// API client for Loremail

const API_BASE = '/api';

export interface ApiError {
  error: string;
  details?: Record<string, string[]>;
}

export interface User {
  id: string;
  email: string;
  role: string;
}

export interface WorldStatus {
  world: {
    id: string;
    inboundAddress: string;
    correspondentName: string;
    timezone: string;
    deliveryTime: string;
    dayIndex: number;
    status: string;
    createdAt: string;
  };
  lastLetterIn: {
    id: string;
    subject: string | null;
    receivedAt: string;
  } | null;
  lastLetterOut: {
    id: string;
    type: string;
    subject: string;
    sentAt: string | null;
  } | null;
  todayLetterSent: boolean;
  stamps?: {
    remaining: number;
    monthlyAllowance: number;
    usedThisPeriod: number;
    periodEnds: string;
  };
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const headers = new Headers(options?.headers);
  if (typeof options?.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error ?? 'An unexpected error occurred');
  }

  return data;
}

// Auth API
export const authApi = {
  requestMagicLink: async (email: string) => {
    return fetchApi<{
      message: string;
      status: 'waitlisted' | 'magic_link_sent';
    }>('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  verifyMagicLink: async (token: string) => {
    return fetchApi<{
      message: string;
      user: User;
      isNewUser: boolean;
    }>(`/auth/verify?token=${encodeURIComponent(token)}`);
  },

  getCurrentUser: async () => {
    return fetchApi<{ user: User }>('/auth/me');
  },

  logout: async () => {
    return fetchApi<{ message: string }>('/auth/logout', {
      method: 'POST',
    });
  },
};

// World API
export const worldApi = {
  getStatus: async () => {
    return fetchApi<WorldStatus>('/world/status');
  },

  getLetters: async (params?: {
    page?: number;
    limit?: number;
    type?: 'in' | 'out' | 'all';
    letterType?: 'daily' | 'confirmation';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.type) searchParams.set('type', params.type);
    if (params?.letterType) searchParams.set('letterType', params.letterType);

    const query = searchParams.toString();
    return fetchApi<{
      letters: Array<{
        id: string;
        direction: 'in' | 'out';
        subject: string | null;
        preview: string;
        date: string;
        type?: string;
        thumbnailUrl?: string | null;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(`/world/letters${query ? `?${query}` : ''}`);
  },

  getLetter: async (direction: 'in' | 'out' | string, id: string) => {
    return fetchApi<{
      id: string;
      direction: string;
      subject: string | null;
      content: string;
      date: string;
      type?: string;
    }>(`/world/letters/${direction}/${id}`);
  },

  updateSettings: async (settings: { timezone?: string; deliveryTime?: string }) => {
    return fetchApi<{ message: string; settings: typeof settings }>('/world/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  },

  composeLetter: async (subject: string | undefined, body: string) => {
    return fetchApi<{ message: string; letterId: string }>('/world/compose', {
      method: 'POST',
      body: JSON.stringify({ subject, body }),
    });
  },
};

// Admin API Types
export interface AdminOverview {
  environment: string;
  version: string;
  gitCommit: string | null;
  uptime: number;
  serverTime: {
    utc: string;
    timezone: string;
  };
  queues: Array<{
    name: string;
    active: number;
    waiting: number;
    delayed: number;
    failed: number;
    completed: number;
    paused: number;
    stalled: number;
    isPaused: boolean;
  }>;
  stats: {
    totalUsers: number;
    totalWorlds: number;
    activeWorlds: number;
    lettersInLast24h: number;
    lettersOutLast24h: number;
  };
  lastActivity: {
    lastInboundAt: string | null;
    lastOutboundAt: string | null;
  };
  recentLetters: Array<{
    id: string;
    direction: 'in' | 'out';
    worldId: string;
    worldAddress: string;
    isQa: boolean;
    subject: string | null;
    fromEmail: string | null;
    createdAt: string;
  }>;
  alerts: Array<{
    type: 'error' | 'warning';
    message: string;
    timestamp: string;
  }>;
}

export interface AdminUser {
  id: string;
  email: string;
  role: 'user' | 'admin';
  status: 'waitlisted' | 'active' | 'suspended' | 'deleted';
  dailyLetterQuota: number;
  worldCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminUserDetail extends AdminUser {
  verifiedSenderEmail: string | null;
  worlds: Array<{
    id: string;
    inboundAddress: string;
    timezone: string;
    deliveryTime: string;
    dayIndex: number;
    lastInboundAt: string | null;
    createdAt: string;
    lettersInCount: number;
    lettersOutCount: number;
  }>;
}

export interface AdminWorld {
  id: string;
  inboundAddress: string;
  timezone: string;
  deliveryTime: string;
  dayIndex: number;
  lastInboundAt: string | null;
  createdAt: string;
  thumbnailUrl: string | null;
  thumbnailPrompt: string | null;
  user: {
    id: string;
    email: string;
  };
  counts: {
    lettersIn: number;
    lettersOut: number;
  };
}

export interface AdminWorldDetail {
  id: string;
  inboundAddress: string;
  inboundToken: string;
  timezone: string;
  deliveryTime: string;
  addressMode: string;
  dayIndex: number;
  autoReviewEnabled: boolean;
  lastInboundAt: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    status: string;
  };
  latestState: {
    id: string;
    version: number;
    updatedAt: string;
    thumbnailUrl: string | null;
    thumbnailPrompt: string | null;
    worldVisualUrl: string | null;
    worldVisualPrompt: string | null;
    worldVisualModel: string | null;
  } | null;
  recentLettersIn: Array<{
    id: string;
    subject: string | null;
    fromEmail: string;
    receivedAt: string;
    processedAt: string | null;
    status: string;
    rejectedReason: string | null;
  }>;
  recentLettersOut: Array<{
    id: string;
    type: string;
    subject: string;
    status: string;
    sentAt: string | null;
    createdAt: string;
  }>;
}

export interface AdminAuditLog {
  id: string;
  adminUser: {
    id: string;
    email: string;
  };
  action: string;
  targetType: string;
  targetId: string;
  details: unknown;
  createdAt: string;
}

// Letters In types
export interface AdminLetterIn {
  id: string;
  worldId: string;
  worldAddress: string;
  userEmail: string;
  isQa: boolean;
  fromEmail: string;
  subject: string | null;
  status: string;
  rejectedReason: string | null;
  errorReason?: string | null;
  receivedAt: string;
  processedAt: string | null;
}

export interface AdminLetterInDetail extends AdminLetterIn {
  user: { id: string; email: string };
  toEmail: string;
  cleanText: string;
  rawRfc822: string;
  hash: string;
  messageId: string | null;
  inboundDay: string;
  answeredByLetterId: string | null;
}

export interface AdminLetterInExtraction {
  id: string;
  status: string;
  extracted: boolean;
  worldStateVersion?: number;
  mergeReport: unknown;
  processedAt: string | null;
  diffCreatedAt?: string;
  extraction: unknown | null;
  errorReason?: string | null;
}

// Letters Out types
export interface AdminLetterOut {
  id: string;
  worldId: string;
  worldAddress: string;
  userEmail: string;
  isQa: boolean;
  type: string;
  subject: string;
  status: string;
  retryCount: number;
  errorMessage: string | null;
  scheduledFor: string;
  createdAt: string;
  sentAt: string | null;
  autoReview: {
    rating: number;
    confidenceLevel: string;
  } | null;
}

export interface AnsweredLetterIn {
  id: string;
  fromEmail: string;
  subject: string | null;
  cleanText: string | null;
  receivedAt: string;
  processedAt: string | null;
  extraction: unknown | null;
}

export interface AdminLetterOutDetail extends AdminLetterOut {
  user: { id: string; email: string };
  bodyText: string;
  smtpMessageId: string | null;
  extraction: unknown | null;
  answeredLetters: AnsweredLetterIn[];
}

// Letter Rating types
export interface LetterRating {
  id: string;
  letterOutId: string;
  rating: number;
  notes: string | null;
  narrativeQuality: number | null;
  consistency: number | null;
  voiceAccuracy: number | null;
  engagement: number | null;
  rater: { id: string; email: string };
  createdAt: string;
  updatedAt: string;
}

export interface LetterRatingInput {
  rating: number;
  notes?: string;
  narrativeQuality?: number;
  consistency?: number;
  voiceAccuracy?: number;
  engagement?: number;
}

// Letter Auto Review types
export interface LetterAutoReview {
  id: string;
  letterOutId: string;
  rating: number;
  overallAssessment: string;
  dimensionScores: {
    authenticity_of_voice: number;
    emotional_realism: number;
    relational_specificity: number;
    spontaneity_vs_structure: number;
    language_naturalness: number;
    intent_motivation_plausibility: number;
  };
  keyStrengths: string[];
  keyWeaknesses: string[];
  recommendations: string[];
  confidenceLevel: 'High' | 'Medium' | 'Low';
  flags?: {
    genericity_risk: number;
    over_polished_risk: number;
    excessive_validation_risk: number;
    emotional_flatness_risk: number;
    formulaic_structure_risk: number;
    meta_explanation_risk: number;
  } | null;
  evidence?: {
    quotes: Array<{
      excerpt: string;
      rationale: string;
      tag: string;
    }>;
  } | null;
  model: string | null;
  provider: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LetterAutoReviewBatchResult {
  queued: string[];
  skipped: Array<{ id: string; reason: string }>;
  missing: string[];
  forced: boolean;
}

// World State types
export interface AdminWorldState {
  id: string;
  worldId: string;
  version: number;
  updatedAt: string;
  canon: unknown;
  style: unknown;
  correspondent?: unknown;
  summaries: unknown;
  archives?: unknown;
  constraints: unknown;
  thumbnailUuid?: string | null;
  thumbnailPath?: string | null;
  thumbnailUrl?: string | null;
  thumbnailPrompt?: string | null;
  worldVisualUuid?: string | null;
  worldVisualPath?: string | null;
  worldVisualUrl?: string | null;
  worldVisualPrompt?: string | null;
  worldVisualModel?: string | null;
  diffs?: Array<{
    id: string;
    letterInId: string | null;
    mergeReport: unknown;
    createdAt: string;
  }>;
}

export interface AdminWorldStateDiff {
  id: string;
  worldId: string;
  worldStateId: string;
  worldStateVersion: number;
  letterInId: string | null;
  mergeReport: unknown;
  createdAt: string;
}

export interface AdminThumbnailGeneration {
  id: string;
  worldId: string;
  worldStateId: string | null;
  letterOutId: string | null;
  prompt: string | null;
  imageUuid: string | null;
  imagePath: string | null;
  imageUrl: string | null;
  attempts: number;
  textDetected: boolean | null;
  qualityRejected: boolean | null;
  qualityReasons: unknown;
  status: 'success' | 'rejected' | 'failed';
  errorMessage: string | null;
  createdAt: string;
}

export interface AdminWorldVisualGeneration {
  id: string;
  worldId: string;
  worldStateId: string | null;
  worldStateVersion: number | null;
  prompt: string | null;
  imageUuid: string | null;
  imagePath: string | null;
  imageUrl: string | null;
  model: string | null;
  status: 'success' | 'failed';
  errorMessage: string | null;
  createdAt: string;
}

export interface AdminWorldEvolution {
  id: string;
  createdAt: string;
  worldStateVersion: number;
  sourceLetterOutId: string | null;
  sourceLetterInId: string | null;
  mergeReport: unknown;
  letterOut: {
    id: string;
    subject: string;
    status: string;
    createdAt: string;
    sentAt: string | null;
  } | null;
  llm: unknown | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Admin API
export const adminApi = {
  // Overview
  getOverview: async () => {
    return fetchApi<AdminOverview>('/admin/overview');
  },

  // Users
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    status?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.role) searchParams.set('role', params.role);
    if (params?.status) searchParams.set('status', params.status);
    const query = searchParams.toString();
    return fetchApi<PaginatedResponse<AdminUser>>(`/admin/users${query ? `?${query}` : ''}`);
  },

  getUser: async (id: string) => {
    return fetchApi<AdminUserDetail>(`/admin/users/${id}`);
  },

  updateUser: async (
    id: string,
    data: { role?: string; status?: string; dailyLetterQuota?: number },
  ) => {
    return fetchApi<{ message: string; user: AdminUser }>(`/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  importWorldForUser: async (id: string, payload: Record<string, unknown> | FormData) => {
    return fetchApi<{
      message: string;
      worldId: string;
      inboundToken: string;
      inboundAddress: string;
      note?: string;
    }>(`/admin/users/${id}/worlds/import`, {
      method: 'POST',
      body: payload instanceof FormData ? payload : JSON.stringify(payload),
    });
  },

  // Worlds
  getWorlds: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    userId?: string;
    hasActivity?: boolean;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.userId) searchParams.set('userId', params.userId);
    if (params?.hasActivity !== undefined)
      searchParams.set('hasActivity', params.hasActivity.toString());
    const query = searchParams.toString();
    return fetchApi<PaginatedResponse<AdminWorld>>(`/admin/worlds${query ? `?${query}` : ''}`);
  },

  getWorld: async (id: string) => {
    return fetchApi<AdminWorldDetail>(`/admin/worlds/${id}`);
  },

  updateWorldStatus: async (id: string, data: { enabled: boolean; reason: string }) => {
    return fetchApi<{ message: string; worldId: string; enabled: boolean }>(
      `/admin/worlds/${id}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  },

  getWorldState: async (id: string) => {
    return fetchApi<AdminWorldState>(`/admin/worlds/${id}/state`);
  },

  getWorldStateVersions: async (id: string, limit?: number) => {
    const query = limit ? `?limit=${limit}` : '';
    return fetchApi<{
      worldId: string;
      versions: Array<{
        id: string;
        version: number;
        updatedAt: string;
        hasDiff: boolean;
        diffCount: number;
      }>;
    }>(`/admin/worlds/${id}/state/versions${query}`);
  },

  getWorldVisuals: async (id: string, limit?: number) => {
    const query = limit ? `?limit=${limit}` : '';
    return fetchApi<{ visuals: AdminWorldVisualGeneration[] }>(
      `/admin/worlds/${id}/visuals${query}`,
    );
  },

  // Audit Logs
  getAuditLogs: async (params?: {
    page?: number;
    limit?: number;
    adminUserId?: string;
    action?: string;
    targetType?: string;
    targetId?: string;
    fromDate?: string;
    toDate?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.adminUserId) searchParams.set('adminUserId', params.adminUserId);
    if (params?.action) searchParams.set('action', params.action);
    if (params?.targetType) searchParams.set('targetType', params.targetType);
    if (params?.targetId) searchParams.set('targetId', params.targetId);
    if (params?.fromDate) searchParams.set('fromDate', params.fromDate);
    if (params?.toDate) searchParams.set('toDate', params.toDate);
    const query = searchParams.toString();
    return fetchApi<PaginatedResponse<AdminAuditLog>>(
      `/admin/audit-logs${query ? `?${query}` : ''}`,
    );
  },

  getAuditLogActions: async () => {
    return fetchApi<{ actions: string[] }>('/admin/audit-logs/actions');
  },

  // Letters In
  getLettersIn: async (params?: {
    page?: number;
    limit?: number;
    worldId?: string;
    status?: string;
    fromDate?: string;
    toDate?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.worldId) searchParams.set('worldId', params.worldId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.fromDate) searchParams.set('fromDate', params.fromDate);
    if (params?.toDate) searchParams.set('toDate', params.toDate);
    const query = searchParams.toString();
    return fetchApi<PaginatedResponse<AdminLetterIn>>(
      `/admin/letters-in${query ? `?${query}` : ''}`,
    );
  },

  getLetterIn: async (id: string) => {
    return fetchApi<AdminLetterInDetail>(`/admin/letters-in/${id}`);
  },

  getLetterInExtraction: async (id: string) => {
    return fetchApi<AdminLetterInExtraction>(`/admin/letters-in/${id}/extraction`);
  },

  // Letters Out
  getLettersOut: async (params?: {
    page?: number;
    limit?: number;
    worldId?: string;
    type?: string;
    status?: string;
    fromDate?: string;
    toDate?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.worldId) searchParams.set('worldId', params.worldId);
    if (params?.type) searchParams.set('type', params.type);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.fromDate) searchParams.set('fromDate', params.fromDate);
    if (params?.toDate) searchParams.set('toDate', params.toDate);
    const query = searchParams.toString();
    return fetchApi<PaginatedResponse<AdminLetterOut>>(
      `/admin/letters-out${query ? `?${query}` : ''}`,
    );
  },

  getLetterOut: async (id: string) => {
    return fetchApi<AdminLetterOutDetail>(`/admin/letters-out/${id}`);
  },

  // Letter Ratings
  getLetterRating: async (letterOutId: string) => {
    return fetchApi<LetterRating>(`/admin/letters-out/${letterOutId}/rating`);
  },

  getLetterAutoReview: async (letterOutId: string) => {
    return fetchApi<LetterAutoReview>(`/admin/letters-out/${letterOutId}/auto-review`);
  },

  requestLetterAutoReview: async (letterOutId: string, force?: boolean) => {
    const query = force ? '?force=true' : '';
    return fetchApi<{ message: string; forced: boolean }>(
      `/admin/letters-out/${letterOutId}/auto-review${query}`,
      {
        method: 'POST',
      },
    );
  },

  requestLetterAutoReviewBatch: async (payload: {
    letterOutIds: string[];
    force?: boolean;
  }) => {
    return fetchApi<LetterAutoReviewBatchResult>(`/admin/letters-out/auto-review/batch`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  submitLetterRating: async (letterOutId: string, data: LetterRatingInput) => {
    return fetchApi<LetterRating>(`/admin/letters-out/${letterOutId}/rating`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  deleteLetterRating: async (letterOutId: string) => {
    return fetchApi<{ message: string }>(`/admin/letters-out/${letterOutId}/rating`, {
      method: 'DELETE',
    });
  },

  // World State (extended)
  getWorldStateVersion: async (worldId: string, version: number) => {
    return fetchApi<AdminWorldState>(`/admin/worlds/${worldId}/state/${version}`);
  },

  getWorldStateDiff: async (worldId: string, diffId: string) => {
    return fetchApi<AdminWorldStateDiff>(`/admin/worlds/${worldId}/state/diff/${diffId}`);
  },

  getThumbnailGenerations: async (params?: {
    page?: number;
    limit?: number;
    status?: 'success' | 'rejected' | 'failed';
    worldId?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.worldId) searchParams.set('worldId', params.worldId);
    const query = searchParams.toString();
    return fetchApi<PaginatedResponse<AdminThumbnailGeneration>>(
      `/admin/thumbnails${query ? `?${query}` : ''}`,
    );
  },

  getWorldEvolutions: async (worldId: string, limit?: number) => {
    const searchParams = new URLSearchParams();
    if (limit) searchParams.set('limit', limit.toString());
    const query = searchParams.toString();
    return fetchApi<{ worldId: string; evolutions: AdminWorldEvolution[] }>(
      `/admin/worlds/${worldId}/evolutions${query ? `?${query}` : ''}`,
    );
  },

  // Admin Actions
  triggerGeneration: async (worldId: string, reason: string) => {
    return fetchApi<{ message: string; worldId: string; jobDate: string }>(
      '/admin/actions/generate-daily',
      {
        method: 'POST',
        body: JSON.stringify({ worldId, reason }),
      },
    );
  },

  reExtract: async (letterInId: string, reason: string) => {
    return fetchApi<{ message: string; letterInId: string }>('/admin/actions/re-extract', {
      method: 'POST',
      body: JSON.stringify({ letterInId, reason }),
    });
  },

  resend: async (letterOutId: string, reason: string) => {
    return fetchApi<{ message: string; letterOutId: string }>('/admin/actions/resend', {
      method: 'POST',
      body: JSON.stringify({ letterOutId, reason }),
    });
  },

  queueLetterNow: async (letterOutId: string, reason: string) => {
    return fetchApi<{ message: string; letterOutId: string }>('/admin/actions/queue-now', {
      method: 'POST',
      body: JSON.stringify({ letterOutId, reason }),
    });
  },

  evolveWorld: async (worldId: string, reason: string, focus?: string) => {
    return fetchApi<{ message: string; worldId: string }>('/admin/actions/evolve-world', {
      method: 'POST',
      body: JSON.stringify({ worldId, reason, focus }),
    });
  },

  compactWorld: async (worldId: string, reason: string, force?: boolean) => {
    return fetchApi<{ message: string; worldId: string }>('/admin/actions/compact-world', {
      method: 'POST',
      body: JSON.stringify({ worldId, reason, force }),
    });
  },

  generateWorldThumbnail: async (worldId: string, reason?: string) => {
    return fetchApi<{ message: string; worldId: string }>('/admin/actions/generate-thumbnail', {
      method: 'POST',
      body: JSON.stringify({ worldId, reason }),
    });
  },

  generateWorldVisual: async (worldId: string, reason?: string) => {
    return fetchApi<{ message: string; worldId: string }>(
      '/admin/actions/generate-world-visual',
      {
        method: 'POST',
        body: JSON.stringify({ worldId, reason }),
      },
    );
  },

  resetUser: async (userId: string, reason: string) => {
    return fetchApi<{
      message: string;
      userId: string;
      worldsDeleted: number;
      previousQuota: number;
    }>('/admin/actions/reset-user', {
      method: 'POST',
      body: JSON.stringify({ userId, reason }),
    });
  },

  deleteWorld: async (worldId: string, reason: string) => {
    return fetchApi<{ message: string; worldId: string }>('/admin/actions/delete-world', {
      method: 'POST',
      body: JSON.stringify({ worldId, reason }),
    });
  },

  // World Style
  updateWorldStyle: async (
    worldId: string,
    data: {
      voice?: string;
      pov?: 'first_person' | 'second_person' | 'third_person_limited' | 'third_person_omniscient';
      tone?: string[];
      formality?: number;
      lexicon?: string[];
      forbidden?: string[];
      formatting?: {
        max_paragraphs?: number;
        include_salutation?: boolean;
        include_signoff?: boolean;
        include_date_stamp?: boolean;
      };
      reason: string;
    },
  ) => {
    return fetchApi<{
      message: string;
      worldId: string;
      styleProfile: unknown;
    }>(`/admin/worlds/${worldId}/style`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  updateWorldAutoReview: async (
    worldId: string,
    data: {
      enabled: boolean;
      reason: string;
    },
  ) => {
    return fetchApi<{ message: string; worldId: string; autoReviewEnabled: boolean }>(
      `/admin/worlds/${worldId}/auto-review`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  },

  previewWorldTokenRotation: async (worldId: string) => {
    return fetchApi<{
      worldId: string;
      currentToken: string;
      proposedToken: string;
    }>(`/admin/worlds/${worldId}/rotate-token/preview`, {
      method: 'POST',
    });
  },

  rotateWorldToken: async (
    worldId: string,
    data: {
      token: string;
      reason: string;
    },
  ) => {
    return fetchApi<{
      message: string;
      worldId: string;
      inboundToken: string;
      previousToken: string;
    }>(`/admin/worlds/${worldId}/rotate-token`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // World LLM Settings
  getWorldLLMSettings: async (worldId: string) => {
    return fetchApi<{
      worldId: string;
      settings: {
        provider: string | null;
        modelPackage: string | null;
        generateModel: string | null;
        reviewModel: string | null;
        writingPolishModel: string | null;
        worldBuilderModel: string | null;
        utilityModel: string | null;
        embeddingModel: string | null;
        embeddingDimension: number;
      };
      resolved: {
        provider: string;
        generateModel: string;
        reviewModel: string;
        writingPolishModel: string | null;
        worldBuilderModel: string;
        utilityModel: string;
        embeddingModel: string;
        embeddingDimension: number;
      };
      systemDefaults: {
        provider: string;
        openai: {
          generateModel: string;
          reviewModel: string;
          writingPolishModel: string | null;
          utilityModel: string;
          worldBuilderModel: string;
          embeddingModel: string;
        };
        fireworks: {
          generateModel: string;
          reviewModel: string;
          writingPolishModel: string | null;
          utilityModel: string;
          worldBuilderModel: string;
          embeddingModel: string;
        };
      };
      availablePackages: Array<{
        key: string;
        name: string;
        provider: string;
        tier: string;
      }>;
    }>(`/admin/worlds/${worldId}/llm`);
  },

  updateWorldLLMSettings: async (
    worldId: string,
    data: {
      modelPackage?: string | null;
      provider?: string | null;
      generateModel?: string | null;
      reviewModel?: string | null;
      writingPolishModel?: string | null;
      worldBuilderModel?: string | null;
      utilityModel?: string | null;
      embeddingModel?: string | null;
      embeddingDimension?: number;
      reason: string;
    },
  ) => {
    return fetchApi<{
      message: string;
      worldId: string;
      settings: {
        modelPackage: string | null;
        provider: string | null;
        generateModel: string | null;
        reviewModel: string | null;
        writingPolishModel: string | null;
        worldBuilderModel: string | null;
        utilityModel: string | null;
        embeddingModel: string | null;
        embeddingDimension: number;
      };
    }>(`/admin/worlds/${worldId}/llm`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // QA Testing
  getQAConfig: async () => {
    return fetchApi<{
      packages: Array<{
        key: string;
        name: string;
        provider: string;
        tier: string;
        models: {
          writing: string;
          worldBuilding: string;
          utility: string;
          embedding: string;
        };
      }>;
      letters: Array<{
        key: string;
        name: string;
        language: string;
        type: string;
        preview: string;
        wordCount: number;
        seriesCount: number;
      }>;
      systemDefaults: {
        provider: string;
        package: string;
      };
    }>('/admin/qa/config');
  },

  getQARuns: async (limit?: number) => {
    const params = limit ? `?limit=${limit}` : '';
    return fetchApi<{
      runs: Array<{
        worldId: string;
        token: string;
        seriesKey: string | null;
        seriesName: string | null;
        seriesType: string | null;
        package: string | null;
        resolvedPackage: string | null;
        provider: string | null;
        createdAt: string;
        hasOutput: boolean;
        thumbnailUrl: string | null;
        letterIn: {
          id: string;
          preview: string;
          extractionStatus: string;
        } | null;
        letterOut: {
          id: string;
          subject: string;
          status: string;
          preview: string;
          wordCount: number;
        } | null;
        series: {
          total: number;
          completed: number;
        } | null;
        stateVersion: number;
      }>;
    }>(`/admin/qa/runs${params}`);
  },

  getQARunDetail: async (worldId: string) => {
    return fetchApi<{
      world: {
        id: string;
        token: string;
        seriesKey: string | null;
        seriesName: string | null;
        seriesType: string | null;
        package: string | null;
        resolvedPackage: string | null;
        provider: string | null;
        createdAt: string;
      };
      resolvedConfig: {
        provider: string;
        generateModel: string;
        worldBuilderModel: string;
        utilityModel: string;
        embeddingModel: string;
        embeddingDimension: number;
      };
      input: {
        id: string;
        text: string;
        extraction: unknown;
        createdAt: string;
      } | null;
      output: {
        id: string;
        subject: string;
        text: string;
        plan: unknown;
        consistency: unknown;
        createdAt: string;
      } | null;
      qualityMetrics: {
        wordCount: number;
        sentenceCount: number;
        paragraphCount: number;
        avgWordsPerSentence: number;
        hasGreeting: boolean;
        hasSignoff: boolean;
        firstPerson: number;
      } | null;
      series: {
        total: number;
        completed: number;
        nextDay: number | null;
        days: Array<{
          day: number;
          letterInId: string;
          letterOutId: string | null;
          status: string;
          sentAt: string | null;
          receivedAt: string;
        }>;
      } | null;
      worldState: {
        version: number;
        canon: unknown;
        diffs: unknown[];
      } | null;
    }>(`/admin/qa/runs/${worldId}`);
  },

  startQARun: async (data: {
    letterKey: 'en' | 'de' | 'en-space' | 'en-stress' | 'en-first-contact';
    packageKey?: string | null;
    day?: number;
    sendAll?: boolean;
    count?: number;
  }) => {
    return fetchApi<{
      message: string;
      worldId: string;
      token: string;
      letterInId: string | null;
      config: {
        letter: string;
        package: string | null;
        day: number;
        sendAll: boolean;
        lettersQueued: number;
        count: number;
      };
    }>('/admin/qa/run', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getQAStatus: async (worldId: string) => {
    return fetchApi<{
      worldId: string;
      status:
        | 'pending'
        | 'extracting'
        | 'evolving'
        | 'deepening'
        | 'generating'
        | 'completed'
        | 'failed';
      generationStage: string | null;
      hasExtraction: boolean;
      hasOutput: boolean;
      stateVersion: number;
      thumbnailUrl: string | null;
      queues: Record<
        string,
        { waiting: number; active: number; completed: number; failed: number }
      >;
    }>(`/admin/qa/status/${worldId}`);
  },

  deleteQARun: async (worldId: string) => {
    return fetchApi<{ message: string; worldId: string }>(`/admin/qa/runs/${worldId}`, {
      method: 'DELETE',
    });
  },

  deleteAllQARuns: async () => {
    return fetchApi<{ message: string; deleted: number }>(`/admin/qa/runs`, {
      method: 'DELETE',
      body: JSON.stringify({}),
    });
  },

  // Analytics
  getAnalyticsOverview: async (params?: {
    from?: string;
    to?: string;
    tenantId?: string;
    worldId?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.tenantId) searchParams.set('tenantId', params.tenantId);
    if (params?.worldId) searchParams.set('worldId', params.worldId);
    const query = searchParams.toString();
    return fetchApi<AnalyticsOverview>(`/admin/analytics/overview${query ? `?${query}` : ''}`);
  },

  getAnalyticsCostByDay: async (params?: {
    from?: string;
    to?: string;
    tenantId?: string;
    worldId?: string;
    provider?: string;
    feature?: string;
    splitBy?: 'feature' | 'model' | 'provider';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.tenantId) searchParams.set('tenantId', params.tenantId);
    if (params?.worldId) searchParams.set('worldId', params.worldId);
    if (params?.provider) searchParams.set('provider', params.provider);
    if (params?.feature) searchParams.set('feature', params.feature);
    if (params?.splitBy) searchParams.set('splitBy', params.splitBy);
    const query = searchParams.toString();
    return fetchApi<CostByDayResponse>(`/admin/analytics/cost-by-day${query ? `?${query}` : ''}`);
  },

  getAnalyticsBreakdown: async (params?: {
    from?: string;
    to?: string;
    tenantId?: string;
    worldId?: string;
    provider?: string;
    feature?: string;
    groupBy?: 'model' | 'feature' | 'provider' | 'world';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.tenantId) searchParams.set('tenantId', params.tenantId);
    if (params?.worldId) searchParams.set('worldId', params.worldId);
    if (params?.provider) searchParams.set('provider', params.provider);
    if (params?.feature) searchParams.set('feature', params.feature);
    if (params?.groupBy) searchParams.set('groupBy', params.groupBy);
    const query = searchParams.toString();
    return fetchApi<AnalyticsBreakdown>(`/admin/analytics/breakdown${query ? `?${query}` : ''}`);
  },

  getAnalyticsPerformance: async (params?: {
    from?: string;
    to?: string;
    tenantId?: string;
    worldId?: string;
    provider?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.tenantId) searchParams.set('tenantId', params.tenantId);
    if (params?.worldId) searchParams.set('worldId', params.worldId);
    if (params?.provider) searchParams.set('provider', params.provider);
    const query = searchParams.toString();
    return fetchApi<AnalyticsPerformance>(
      `/admin/analytics/performance${query ? `?${query}` : ''}`,
    );
  },

  getLetterCost: async (letterId: string) => {
    return fetchApi<LetterCostDetail>(`/admin/analytics/letters/${letterId}/cost`);
  },

  clearAnalytics: async () => {
    // Use fetch directly to avoid Content-Type: application/json header
    // which Fastify rejects for empty body DELETE requests
    const response = await fetch(`${API_BASE}/admin/analytics/clear?confirm=true`, {
      method: 'DELETE',
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error ?? 'Failed to clear analytics');
    }
    return data as {
      message: string;
      deleted: {
        events: number;
        stages: number;
        letterCosts: number;
        rollups: number;
      };
    };
  },

  // Budgets
  getBudgets: async () => {
    return fetchApi<{ budgets: Budget[] }>('/admin/budgets');
  },

  getBudget: async (id: string) => {
    return fetchApi<{
      budget: Budget;
      usageHistory: Array<{ month: string; costUsd: number; costFormatted: string }>;
    }>(`/admin/budgets/${id}`);
  },

  createBudget: async (data: {
    tenantId?: string;
    worldId?: string;
    monthlyLimitUsd?: number;
    alertThreshold?: number;
    enabled?: boolean;
  }) => {
    return fetchApi<{ budget: Budget }>('/admin/budgets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateBudget: async (
    id: string,
    data: {
      monthlyLimitUsd?: number | null;
      alertThreshold?: number;
      enabled?: boolean;
    },
  ) => {
    return fetchApi<{ budget: Budget }>(`/admin/budgets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteBudget: async (id: string) => {
    return fetchApi<void>(`/admin/budgets/${id}`, {
      method: 'DELETE',
    });
  },

  // Rate Cards
  getRates: async () => {
    return fetchApi<RateCardResponse>('/admin/analytics/rates');
  },

  triggerRateSync: async () => {
    return fetchApi<{ message: string; jobName: string }>('/admin/analytics/rates/sync', {
      method: 'POST',
    });
  },

  getRateHistory: async (limit?: number) => {
    const params = limit ? `?limit=${limit}` : '';
    return fetchApi<RateCardHistoryResponse>(`/admin/analytics/rates/history${params}`);
  },

  // Letter Costs
  getLetterCosts: async (params?: { from?: string; to?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    const query = searchParams.toString();
    return fetchApi<LetterCostsResponse>(
      `/admin/analytics/letter-costs${query ? `?${query}` : ''}`,
    );
  },
};

// Analytics types
export interface AnalyticsOverview {
  kpis: {
    totalCost: {
      value: number;
      formatted: string;
      trend: number;
    };
    totalRequests: {
      value: number;
      trend: number;
    };
    totalTokens: {
      value: number;
      inputTokens: number;
      outputTokens: number;
    };
    avgLatencyMs: {
      value: number;
    };
    errorRate: {
      value: number;
      errorCount: number;
    };
  };
  period: {
    from: string | null;
    to: string | null;
  };
}

export interface CostByDayEntry {
  date: string;
  cost: number;
  costMicro: number;
  tokens: number;
  requests: number;
  avgLatencyMs: number;
  // Dynamic keys when splitBy is used
  [key: string]: string | number;
}

export interface CostByDayResponse {
  series: CostByDayEntry[];
  dimensions: string[] | null;
  splitBy: string | null;
}

export interface AnalyticsBreakdown {
  groupBy: string;
  items: Array<{
    key: string;
    cost: number;
    costMicro: number;
    requests: number;
    inputTokens: number;
    outputTokens: number;
    avgLatencyMs: number;
    percentage: number;
  }>;
  totalCost: number;
}

export interface AnalyticsPerformance {
  latencyByModel: Array<{
    model: string;
    avgLatencyMs: number;
    minLatencyMs: number;
    maxLatencyMs: number;
    requests: number;
  }>;
  errorRates: Array<{
    feature: string;
    totalRequests: number;
    errorCount: number;
    errorRate: number;
  }>;
  recentErrors: Array<{
    id: string;
    timestamp: string;
    feature: string;
    model: string;
    errorType: string | null;
    worldId: string | null;
  }>;
}

export interface LetterCostDetail {
  letterId: string;
  totalCost: number;
  totalCostFormatted: string;
  totalTokens: number;
  totalLatencyMs?: number;
  stages?: Array<{
    stage: string;
    provider: string;
    model: string;
    inputTokens: number;
    outputTokens: number;
    latencyMs: number;
    cost: number;
    success: boolean;
    events: Array<{
      id: string;
      feature: string;
      model: string;
      inputTokens: number;
      outputTokens: number;
      latencyMs: number;
      cost: number;
      success: boolean;
      timestamp: string;
    }>;
  }>;
  events?: Array<{
    id: string;
    feature: string;
    model: string;
    inputTokens: number;
    outputTokens: number;
    latencyMs: number;
    cost: number;
    success: boolean;
    timestamp: string;
  }>;
}

export interface Budget {
  id: string;
  tenantId: string | null;
  tenantEmail?: string | null;
  worldId: string | null;
  worldName?: string | null;
  monthlyLimitUsd: number | null;
  monthlyLimitFormatted?: string;
  alertThreshold: number;
  currentMonthUsd: number;
  currentMonthFormatted?: string;
  usagePercent: number | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// Rate card types
export interface ModelRate {
  inputPer1M: number;
  outputPer1M?: number;
}

export interface RateCardInfo {
  id: string;
  version: string;
  effectiveFrom: string;
  createdAt: string;
  modelCount: number;
}

export interface RateCardResponse {
  rateCard: RateCardInfo;
  rates: Record<string, ModelRate>;
  grouped: {
    openai: Record<string, ModelRate>;
    fireworks: Record<string, ModelRate>;
    groq: Record<string, ModelRate>;
    other: Record<string, ModelRate>;
  };
  providerCounts: {
    openai: number;
    fireworks: number;
    groq: number;
    other: number;
  };
}

export interface RateCardHistoryResponse {
  history: RateCardInfo[];
}

// Letter cost types
export interface LetterCostSummary {
  totalLetters: number;
  totalCost: number;
  totalCostFormatted: string;
  totalTokens: number;
  avgCostPerLetter: number;
  avgCostPerLetterFormatted: string;
  minCostPerLetter: number;
  maxCostPerLetter: number;
  avgTokensPerLetter: number;
  avgLatencyMs: number;
  avgLetterLength: number;
  avgCostPerCharMicro: number | null;
}

export interface LetterCostStageBreakdown {
  stage: string;
  totalCost: number;
  avgCost: number;
  avgLatencyMs: number;
  totalTokens: number;
  count: number;
  percentage: number;
}

export interface LetterCostPackageBreakdown {
  package: string;
  totalCost: number;
  avgCost: number;
  avgLatencyMs: number;
  totalTokens: number;
  count: number;
  percentage: number;
}

export interface LetterCostDistribution {
  label: string;
  count: number;
  percentage: number;
}

export interface RecentLetterCost {
  id: string;
  letterInId: string | null;
  letterOutId: string | null;
  worldId: string;
  isQa: boolean;
  letterLength: number | null;
  totalTokens: number;
  totalCost: number;
  totalCostFormatted: string;
  totalLatencyMs: number;
  createdAt: string;
  stages: {
    stage: string;
    cost: number;
    latencyMs: number;
  }[];
}

export interface LetterCostsResponse {
  summary: LetterCostSummary;
  stageBreakdown: LetterCostStageBreakdown[];
  packageBreakdown: LetterCostPackageBreakdown[];
  distribution: LetterCostDistribution[];
  recentLetters: RecentLetterCost[];
}
